import React, { useState, useEffect } from 'react'
import { 
  Plus,
  Save,
  RotateCcw,
  Target,
  DollarSign,
  Users,
  TrendingUp,
  BarChart3,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  Activity,
  MessageSquare,
  Image,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import { campaignMasterService } from '../../services/campaignMasterService'

const CampaignMasterForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [activeTab, setActiveTab] = useState('campaignInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingCampaignCode, setIsLoadingCampaignCode] = useState(false)
  
  const [formData, setFormData] = useState({
    // Campaign Info - Matching backend model
    campaignCode: '',
    campaignName: '',
    campaignDescription: '',
    campaignType: 'Email',
    campaignCategory: 'Awareness',
    campaignStatus: 'Draft',
    campaignPriority: 'Medium',
    startDate: '',
    endDate: '',
    budget: 0,
    actualCost: 0,
    currency: 'INR',
    targetAudience: '',
    campaignObjective: '',
    successMetrics: '',
    targetCompanySize: 'All',
    campaignMessage: '',
    callToAction: '',
    brandGuidelines: '',
    toneOfVoice: 'Professional',
    landingPageUrl: '',
    trackingCode: '',
    notes: ''
  })

  // Dynamic fields
  const [targetRegions, setTargetRegions] = useState(['']);
  const [targetIndustries, setTargetIndustries] = useState(['']);
  const [targetJobTitles, setTargetJobTitles] = useState(['']);
  const [targetKeywords, setTargetKeywords] = useState(['']);
  const [interests, setInterests] = useState(['']);
  const [keyMessages, setKeyMessages] = useState(['']);
  const [tags, setTags] = useState(['']);
  const [categories, setCategories] = useState(['']);
  const [channels, setChannels] = useState([{
    channelName: 'Email',
    channelStatus: 'Active',
    channelBudget: 0,
    channelSpent: 0,
    channelImpressions: 0,
    channelClicks: 0,
    channelConversions: 0
  }]);
  const [creativeAssets, setCreativeAssets] = useState([{
    assetType: 'Image',
    assetName: '',
    assetUrl: '',
    assetSize: '',
    assetDescription: '',
    isPrimary: false
  }]);
  const [teamMembers, setTeamMembers] = useState([{
    user: '',
    role: 'Manager',
    permissions: []
  }]);
  const [stakeholders, setStakeholders] = useState([{
    name: '',
    email: '',
    role: '',
    department: ''
  }]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);

  // Load campaign code on component mount
  useEffect(() => {
    if (!isEdit) {
      loadCampaignCode()
    }
  }, [])

  const loadCampaignCode = async () => {
    try {
      setIsLoadingCampaignCode(true)
      const response = await autoIncrementService.getCurrentValue('CAMPAIGN')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          campaignCode: sequence
        }))
      }
    } catch (error) {
      console.error('Error loading campaign code:', error)
      toast.error('Failed to load campaign code')
    } finally {
      setIsLoadingCampaignCode(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Dynamic field handlers
  const addTargetRegion = () => setTargetRegions([...targetRegions, '']);
  const removeTargetRegion = (index) => {
    if (targetRegions.length > 1) {
      setTargetRegions(targetRegions.filter((_, i) => i !== index));
    }
  };
  const handleTargetRegionChange = (index, value) => {
    const newRegions = [...targetRegions];
    newRegions[index] = value;
    setTargetRegions(newRegions);
  };

  const addTargetIndustry = () => setTargetIndustries([...targetIndustries, '']);
  const removeTargetIndustry = (index) => {
    if (targetIndustries.length > 1) {
      setTargetIndustries(targetIndustries.filter((_, i) => i !== index));
    }
  };
  const handleTargetIndustryChange = (index, value) => {
    const newIndustries = [...targetIndustries];
    newIndustries[index] = value;
    setTargetIndustries(newIndustries);
  };

  const addTargetJobTitle = () => setTargetJobTitles([...targetJobTitles, '']);
  const removeTargetJobTitle = (index) => {
    if (targetJobTitles.length > 1) {
      setTargetJobTitles(targetJobTitles.filter((_, i) => i !== index));
    }
  };
  const handleTargetJobTitleChange = (index, value) => {
    const newJobTitles = [...targetJobTitles];
    newJobTitles[index] = value;
    setTargetJobTitles(newJobTitles);
  };

  const addTargetKeyword = () => setTargetKeywords([...targetKeywords, '']);
  const removeTargetKeyword = (index) => {
    if (targetKeywords.length > 1) {
      setTargetKeywords(targetKeywords.filter((_, i) => i !== index));
    }
  };
  const handleTargetKeywordChange = (index, value) => {
    const newKeywords = [...targetKeywords];
    newKeywords[index] = value;
    setTargetKeywords(newKeywords);
  };

  const addInterest = () => setInterests([...interests, '']);
  const removeInterest = (index) => {
    if (interests.length > 1) {
      setInterests(interests.filter((_, i) => i !== index));
    }
  };
  const handleInterestChange = (index, value) => {
    const newInterests = [...interests];
    newInterests[index] = value;
    setInterests(newInterests);
  };

  const addKeyMessage = () => setKeyMessages([...keyMessages, '']);
  const removeKeyMessage = (index) => {
    if (keyMessages.length > 1) {
      setKeyMessages(keyMessages.filter((_, i) => i !== index));
    }
  };
  const handleKeyMessageChange = (index, value) => {
    const newMessages = [...keyMessages];
    newMessages[index] = value;
    setKeyMessages(newMessages);
  };

  const addTag = () => setTags([...tags, '']);
  const removeTag = (index) => {
    if (tags.length > 1) {
      setTags(tags.filter((_, i) => i !== index));
    }
  };
  const handleTagChange = (index, value) => {
    const newTags = [...tags];
    newTags[index] = value;
    setTags(newTags);
  };

  const addCategory = () => setCategories([...categories, '']);
  const removeCategory = (index) => {
    if (categories.length > 1) {
      setCategories(categories.filter((_, i) => i !== index));
    }
  };
  const handleCategoryChange = (index, value) => {
    const newCategories = [...categories];
    newCategories[index] = value;
    setCategories(newCategories);
  };

  const addChannel = () => setChannels([...channels, {
    channelName: 'Email',
    channelStatus: 'Active',
    channelBudget: 0,
    channelSpent: 0,
    channelImpressions: 0,
    channelClicks: 0,
    channelConversions: 0
  }]);
  const removeChannel = (index) => {
    if (channels.length > 1) {
      setChannels(channels.filter((_, i) => i !== index));
    }
  };
  const handleChannelChange = (index, field, value) => {
    const newChannels = [...channels];
    newChannels[index][field] = value;
    setChannels(newChannels);
  };

  const addCreativeAsset = () => setCreativeAssets([...creativeAssets, {
    assetType: 'Image',
    assetName: '',
    assetUrl: '',
    assetSize: '',
    assetDescription: '',
    isPrimary: false
  }]);
  const removeCreativeAsset = (index) => {
    if (creativeAssets.length > 1) {
      setCreativeAssets(creativeAssets.filter((_, i) => i !== index));
    }
  };
  const handleCreativeAssetChange = (index, field, value) => {
    const newAssets = [...creativeAssets];
    newAssets[index][field] = value;
    setCreativeAssets(newAssets);
  };

  const addTeamMember = () => setTeamMembers([...teamMembers, {
    user: '',
    role: 'Manager',
    permissions: []
  }]);
  const removeTeamMember = (index) => {
    if (teamMembers.length > 1) {
      setTeamMembers(teamMembers.filter((_, i) => i !== index));
    }
  };
  const handleTeamMemberChange = (index, field, value) => {
    const newMembers = [...teamMembers];
    newMembers[index][field] = value;
    setTeamMembers(newMembers);
  };

  const addStakeholder = () => setStakeholders([...stakeholders, {
    name: '',
    email: '',
    role: '',
    department: ''
  }]);
  const removeStakeholder = (index) => {
    if (stakeholders.length > 1) {
      setStakeholders(stakeholders.filter((_, i) => i !== index));
    }
  };
  const handleStakeholderChange = (index, field, value) => {
    const newStakeholders = [...stakeholders];
    newStakeholders[index][field] = value;
    setStakeholders(newStakeholders);
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const campaignData = {
        ...formData,
        targetRegions: targetRegions.filter(r => r.trim()),
        targetIndustries: targetIndustries.filter(i => i.trim()),
        targetJobTitles: targetJobTitles.filter(j => j.trim()),
        targetKeywords: targetKeywords.filter(k => k.trim()),
        interests: interests.filter(i => i.trim()),
        keyMessages: keyMessages.filter(m => m.trim()),
        tags: tags.filter(t => t.trim()),
        categories: categories.filter(c => c.trim()),
        channels: channels.filter(c => c.channelName),
        creativeAssets: creativeAssets.filter(a => a.assetName.trim()),
        teamMembers: teamMembers.filter(m => m.user),
        stakeholders: stakeholders.filter(s => s.name.trim())
      }

      const response = await campaignMasterService.createCampaign(campaignData)
      if (response.success) {
        toast.success('Campaign created successfully')
        navigate('/crm/campaign-master')
      } else {
        toast.error(response.message || 'Failed to create campaign')
      }
    } catch (error) {
      console.error('Error creating campaign:', error)
      toast.error('Failed to create campaign')
    }
  }

  const handleReset = () => {
    setFormData({
      campaignCode: '',
      campaignName: '',
      campaignDescription: '',
      campaignType: 'Email',
      campaignCategory: 'Awareness',
      campaignStatus: 'Draft',
      campaignPriority: 'Medium',
      startDate: '',
      endDate: '',
      budget: 0,
      actualCost: 0,
      currency: 'INR',
      targetAudience: '',
      campaignObjective: '',
      successMetrics: '',
      targetCompanySize: 'All',
      campaignMessage: '',
      callToAction: '',
      brandGuidelines: '',
      toneOfVoice: 'Professional',
      landingPageUrl: '',
      trackingCode: '',
      notes: ''
    })
    setTargetRegions([''])
    setTargetIndustries([''])
    setTargetJobTitles([''])
    setTargetKeywords([''])
    setInterests([''])
    setKeyMessages([''])
    setTags([''])
    setCategories([''])
    setChannels([{
      channelName: 'Email',
      channelStatus: 'Active',
      channelBudget: 0,
      channelSpent: 0,
      channelImpressions: 0,
      channelClicks: 0,
      channelConversions: 0
    }])
    setCreativeAssets([{
      assetType: 'Image',
      assetName: '',
      assetUrl: '',
      assetSize: '',
      assetDescription: '',
      isPrimary: false
    }])
    setTeamMembers([{
      user: '',
      role: 'Manager',
      permissions: []
    }])
    setStakeholders([{
      name: '',
      email: '',
      role: '',
      department: ''
    }])
    loadCampaignCode()
  }

  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Campaign Master',
      path: '/crm/campaign-master',
      onClick: () => navigate('/crm/campaign-master')
    },
    {
      label: 'New Campaign',
      path: '/crm/campaign-master/new',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>What is Campaign Master?</h3>
      <p>Campaign Master lets you <strong>create and manage marketing campaign information</strong>—type, category, dates, budget, target audience, channels, creatives, and team.</p>
      <h3>Tabs</h3>
      <p><strong>Campaign Info</strong> – Code, name, description, type, category, status, priority, start/end dates, budget, objectives, and messaging.</p>
      <p><strong>Targeting</strong> – Target regions, industries, job titles, keywords, interests, and company size.</p>
      <p><strong>Channels &amp; Creatives</strong> – Channel breakdown (e.g. Email) and creative assets (images, URLs, descriptions).</p>
      <p><strong>Team &amp; Stakeholders</strong> – Team members and stakeholder contacts.</p>
      <h3>How to use</h3>
      <p>Fill required fields (e.g. campaign code, name). Use <strong>Create Campaign</strong> to save. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Campaign Master Entry</h1>
          <p>Create and manage campaign information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Campaign Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'campaignInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('campaignInfo')}
          >
            <Target size={18} />
            <span>Campaign Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'targeting' ? 'active' : ''}`}
            onClick={() => setActiveTab('targeting')}
          >
            <Users size={18} />
            <span>Targeting & Demographics</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <MessageSquare size={18} />
            <span>Content & Channels</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'assets' ? 'active' : ''}`}
            onClick={() => setActiveTab('assets')}
          >
            <Image size={18} />
            <span>Assets & Team</span>
          </button>
        </div>
          
        <form className="form-content" onSubmit={handleSubmit}>

          {/* Campaign Info Tab */}
          {activeTab === 'campaignInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Campaign Information</h3>
              </div>
              
              {/* Row 1: Campaign Code, Name, Type, Category */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="campaignCode" className="form-label required">
                    Campaign Code
                  </label>
                  <input
                    type="text"
                    id="campaignCode"
                    name="campaignCode"
                    value={isLoadingCampaignCode ? 'Generating...' : formData.campaignCode}
                    readOnly
                    className="form-input"
                    placeholder="Enter campaign code"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="campaignName" className="form-label required">
                    Campaign Name
                  </label>
                  <input
                    type="text"
                    id="campaignName"
                    name="campaignName"
                    value={formData.campaignName}
                    onChange={(e) => handleInputChange('campaignName', e.target.value)}
                    className="form-input"
                    placeholder="Enter campaign name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="campaignType" className="form-label required">
                    Campaign Type
                  </label>
                  <select
                    id="campaignType"
                    name="campaignType"
                    value={formData.campaignType}
                    onChange={(e) => handleInputChange('campaignType', e.target.value)}
                    className="form-input"
                  >
                    <option value="Email">Email</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Digital">Digital</option>
                    <option value="Traditional">Traditional</option>
                    <option value="Content">Content</option>
                    <option value="Event">Event</option>
                    <option value="Webinar">Webinar</option>
                    <option value="Retargeting">Retargeting</option>
                    <option value="Influencer">Influencer</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="campaignCategory" className="form-label required">
                    Campaign Category
                  </label>
                  <select
                    id="campaignCategory"
                    name="campaignCategory"
                    value={formData.campaignCategory}
                    onChange={(e) => handleInputChange('campaignCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="Awareness">Awareness</option>
                    <option value="Lead Generation">Lead Generation</option>
                    <option value="Nurturing">Nurturing</option>
                    <option value="Conversion">Conversion</option>
                    <option value="Retention">Retention</option>
                    <option value="Reactivation">Reactivation</option>
                    <option value="Brand Building">Brand Building</option>
                    <option value="Product Launch">Product Launch</option>
                    <option value="Seasonal">Seasonal</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Status, Priority, Start Date, End Date */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="campaignStatus" className="form-label">
                    Campaign Status *
                  </label>
                  <select
                    id="campaignStatus"
                    name="campaignStatus"
                    value={formData.campaignStatus}
                    onChange={(e) => handleInputChange('campaignStatus', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="Draft">Draft</option>
                    <option value="Active">Active</option>
                    <option value="Paused">Paused</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="campaignPriority" className="form-label">
                    Campaign Priority *
                  </label>
                  <select
                    id="campaignPriority"
                    name="campaignPriority"
                    value={formData.campaignPriority}
                    onChange={(e) => handleInputChange('campaignPriority', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate" className="form-label">
                    End Date *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              {/* Row 3: Budget, Actual Cost, Currency, Target Audience */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="budget" className="form-label">
                    Budget
                  </label>
                  <input
                    type="number"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="actualCost" className="form-label">
                    Actual Cost
                  </label>
                  <input
                    type="number"
                    id="actualCost"
                    name="actualCost"
                    value={formData.actualCost}
                    onChange={(e) => handleInputChange('actualCost', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="form-input"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="targetAudience" className="form-label">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    id="targetAudience"
                    name="targetAudience"
                    value={formData.targetAudience}
                    onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                    className="form-input"
                    placeholder="Enter target audience"
                  />
                </div>
              </div>

              {/* Row 4: Campaign Objective, Success Metrics */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="campaignObjective" className="form-label">
                    Campaign Objective
                  </label>
                  <textarea
                    id="campaignObjective"
                    name="campaignObjective"
                    value={formData.campaignObjective}
                    onChange={(e) => handleInputChange('campaignObjective', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter campaign objective"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="successMetrics" className="form-label">
                    Success Metrics
                  </label>
                  <textarea
                    id="successMetrics"
                    name="successMetrics"
                    value={formData.successMetrics}
                    onChange={(e) => handleInputChange('successMetrics', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter success metrics"
                  />
                </div>
              </div>

              {/* Row 5: Campaign Description */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="campaignDescription" className="form-label">
                    Campaign Description
                  </label>
                  <textarea
                    id="campaignDescription"
                    name="campaignDescription"
                    value={formData.campaignDescription}
                    onChange={(e) => handleInputChange('campaignDescription', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter campaign description"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Targeting & Demographics Tab */}
          {activeTab === 'targeting' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Targeting & Demographics</h3>
              </div>
              
              {/* Row 1: Target Company Size */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="targetCompanySize" className="form-label">
                    Target Company Size
                  </label>
                  <select
                    id="targetCompanySize"
                    name="targetCompanySize"
                    value={formData.targetCompanySize}
                    onChange={(e) => handleInputChange('targetCompanySize', e.target.value)}
                    className="form-input"
                  >
                    <option value="All">All</option>
                    <option value="Startup">Startup</option>
                    <option value="SMB">SMB</option>
                    <option value="Mid-Market">Mid-Market</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              {/* Target Regions */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <MapPin size={20} className="text-blue-500" />
                    <h4>Target Regions</h4>
                    <span className="field-count">{targetRegions.filter(r => r.trim()).length} regions</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTargetRegion}
                  >
                    <Plus size={16} />
                    Add Region
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {targetRegions.map((region, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={region}
                          onChange={(e) => handleTargetRegionChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter target region (e.g., North India, South India)"
                        />
                        <div className="field-actions">
                          {targetRegions.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTargetRegion(index)}
                              title="Remove region"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Target Industries */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Users size={20} className="text-green-500" />
                    <h4>Target Industries</h4>
                    <span className="field-count">{targetIndustries.filter(i => i.trim()).length} industries</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTargetIndustry}
                  >
                    <Plus size={16} />
                    Add Industry
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {targetIndustries.map((industry, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={industry}
                          onChange={(e) => handleTargetIndustryChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter target industry (e.g., Technology, Healthcare, Finance)"
                        />
                        <div className="field-actions">
                          {targetIndustries.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTargetIndustry(index)}
                              title="Remove industry"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Target Job Titles */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Users size={20} className="text-purple-500" />
                    <h4>Target Job Titles</h4>
                    <span className="field-count">{targetJobTitles.filter(j => j.trim()).length} titles</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTargetJobTitle}
                  >
                    <Plus size={16} />
                    Add Job Title
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {targetJobTitles.map((jobTitle, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={jobTitle}
                          onChange={(e) => handleTargetJobTitleChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter target job title (e.g., CEO, Marketing Manager, Sales Director)"
                        />
                        <div className="field-actions">
                          {targetJobTitles.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTargetJobTitle(index)}
                              title="Remove job title"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Target Keywords */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Tag size={20} className="text-orange-500" />
                    <h4>Target Keywords</h4>
                    <span className="field-count">{targetKeywords.filter(k => k.trim()).length} keywords</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTargetKeyword}
                  >
                    <Plus size={16} />
                    Add Keyword
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {targetKeywords.map((keyword, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={keyword}
                          onChange={(e) => handleTargetKeywordChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter target keyword (e.g., software, consulting, technology)"
                        />
                        <div className="field-actions">
                          {targetKeywords.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTargetKeyword(index)}
                              title="Remove keyword"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Interests */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Tag size={20} className="text-pink-500" />
                    <h4>Interests</h4>
                    <span className="field-count">{interests.filter(i => i.trim()).length} interests</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addInterest}
                  >
                    <Plus size={16} />
                    Add Interest
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {interests.map((interest, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={interest}
                          onChange={(e) => handleInterestChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter interest (e.g., technology, business, innovation)"
                        />
                        <div className="field-actions">
                          {interests.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeInterest(index)}
                              title="Remove interest"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Content & Channels Tab */}
          {activeTab === 'content' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Content & Messaging</h3>
              </div>
              
              {/* Row 1: Campaign Message, Call to Action */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="campaignMessage" className="form-label">
                    Campaign Message
                  </label>
                  <textarea
                    id="campaignMessage"
                    name="campaignMessage"
                    value={formData.campaignMessage}
                    onChange={(e) => handleInputChange('campaignMessage', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter campaign message"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="callToAction" className="form-label">
                    Call to Action
                  </label>
                  <textarea
                    id="callToAction"
                    name="callToAction"
                    value={formData.callToAction}
                    onChange={(e) => handleInputChange('callToAction', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter call to action"
                  />
                </div>
              </div>

              {/* Key Messages */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <MessageSquare size={20} className="text-blue-500" />
                    <h4>Key Messages</h4>
                    <span className="field-count">{keyMessages.filter(m => m.trim()).length} messages</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addKeyMessage}
                  >
                    <Plus size={16} />
                    Add Message
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {keyMessages.map((message, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={message}
                          onChange={(e) => handleKeyMessageChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter key message"
                        />
                        <div className="field-actions">
                          {keyMessages.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeKeyMessage(index)}
                              title="Remove message"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Row 2: Brand Guidelines, Tone of Voice */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="brandGuidelines" className="form-label">
                    Brand Guidelines
                  </label>
                  <textarea
                    id="brandGuidelines"
                    name="brandGuidelines"
                    value={formData.brandGuidelines}
                    onChange={(e) => handleInputChange('brandGuidelines', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter brand guidelines"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="toneOfVoice" className="form-label">
                    Tone of Voice
                  </label>
                  <select
                    id="toneOfVoice"
                    name="toneOfVoice"
                    value={formData.toneOfVoice}
                    onChange={(e) => handleInputChange('toneOfVoice', e.target.value)}
                    className="form-input"
                  >
                    <option value="Professional">Professional</option>
                    <option value="Casual">Casual</option>
                    <option value="Friendly">Friendly</option>
                    <option value="Authoritative">Authoritative</option>
                    <option value="Playful">Playful</option>
                    <option value="Serious">Serious</option>
                    <option value="Inspirational">Inspirational</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Channels Section */}
              <div className="section-header">
                <h3>Channels & Platforms</h3>
              </div>
              
              {/* Campaign Channels */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <BarChart3 size={20} className="text-blue-500" />
                    <h4>Campaign Channels</h4>
                    <span className="field-count">{channels.filter(c => c.channelName).length} channels</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addChannel}
                  >
                    <Plus size={16} />
                    Add Channel
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {channels.map((channel, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <div className="grid-row-2">
                          <input
                            type="text"
                            value={channel.channelName}
                            onChange={(e) => handleChannelChange(index, 'channelName', e.target.value)}
                            className="form-input"
                            placeholder="Channel name (e.g., Email, Social Media, Google Ads)"
                          />
                          <select
                            value={channel.channelStatus}
                            onChange={(e) => handleChannelChange(index, 'channelStatus', e.target.value)}
                            className="form-input"
                          >
                            <option value="Active">Active</option>
                            <option value="Paused">Paused</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </div>
                        <div className="grid-row-4">
                          <input
                            type="number"
                            value={channel.channelBudget}
                            onChange={(e) => handleChannelChange(index, 'channelBudget', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            placeholder="Budget"
                          />
                          <input
                            type="number"
                            value={channel.channelSpent}
                            onChange={(e) => handleChannelChange(index, 'channelSpent', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            placeholder="Spent"
                          />
                          <input
                            type="number"
                            value={channel.channelImpressions}
                            onChange={(e) => handleChannelChange(index, 'channelImpressions', parseInt(e.target.value) || 0)}
                            className="form-input"
                            placeholder="Impressions"
                          />
                          <input
                            type="number"
                            value={channel.channelClicks}
                            onChange={(e) => handleChannelChange(index, 'channelClicks', parseInt(e.target.value) || 0)}
                            className="form-input"
                            placeholder="Clicks"
                          />
                        </div>
                        <div className="grid-row-2">
                          <input
                            type="number"
                            value={channel.channelConversions}
                            onChange={(e) => handleChannelChange(index, 'channelConversions', parseInt(e.target.value) || 0)}
                            className="form-input"
                            placeholder="Conversions"
                          />
                          <div className="field-actions">
                            {channels.length > 1 && (
                              <button 
                                type="button" 
                                className="btn-remove-field"
                                onClick={() => removeChannel(index)}
                                title="Remove channel"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Assets & Team Tab */}
          {activeTab === 'assets' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Creative Assets</h3>
              </div>
              
              {/* Channels */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <BarChart3 size={20} className="text-green-500" />
                    <h4>Marketing Channels</h4>
                    <span className="field-count">{channels.filter(c => c.channelName).length} channels</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addChannel}
                  >
                    <Plus size={16} />
                    Add Channel
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {channels.map((channel, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <div className="grid-row-3">
                          <select
                            value={channel.channelName}
                            onChange={(e) => handleChannelChange(index, 'channelName', e.target.value)}
                            className="form-input"
                          >
                            <option value="Email">Email</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Instagram">Instagram</option>
                            <option value="LinkedIn">LinkedIn</option>
                            <option value="Twitter">Twitter</option>
                            <option value="Google Ads">Google Ads</option>
                            <option value="YouTube">YouTube</option>
                            <option value="TikTok">TikTok</option>
                            <option value="Snapchat">Snapchat</option>
                            <option value="Pinterest">Pinterest</option>
                            <option value="Website">Website</option>
                            <option value="Mobile App">Mobile App</option>
                            <option value="SMS">SMS</option>
                            <option value="Direct Mail">Direct Mail</option>
                            <option value="Print">Print</option>
                            <option value="Radio">Radio</option>
                            <option value="TV">TV</option>
                            <option value="Other">Other</option>
                          </select>
                          <select
                            value={channel.channelStatus}
                            onChange={(e) => handleChannelChange(index, 'channelStatus', e.target.value)}
                            className="form-input"
                          >
                            <option value="Active">Active</option>
                            <option value="Paused">Paused</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                          <input
                            type="number"
                            value={channel.channelBudget}
                            onChange={(e) => handleChannelChange(index, 'channelBudget', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            placeholder="Budget"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="field-actions">
                          {channels.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeChannel(index)}
                              title="Remove channel"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Row 1: Landing Page URL, Tracking Code */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="landingPageUrl" className="form-label">
                    Landing Page URL
                  </label>
                  <input
                    type="url"
                    id="landingPageUrl"
                    name="landingPageUrl"
                    value={formData.landingPageUrl}
                    onChange={(e) => handleInputChange('landingPageUrl', e.target.value)}
                    className="form-input"
                    placeholder="Enter landing page URL"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="trackingCode" className="form-label">
                    Tracking Code
                  </label>
                  <input
                    type="text"
                    id="trackingCode"
                    name="trackingCode"
                    value={formData.trackingCode}
                    onChange={(e) => handleInputChange('trackingCode', e.target.value)}
                    className="form-input"
                    placeholder="Enter tracking code"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Assets Tab */}
          {activeTab === 'assets' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Creative Assets</h3>
              </div>
              
              {/* Creative Assets */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Image size={20} className="text-purple-500" />
                    <h4>Creative Assets</h4>
                    <span className="field-count">{creativeAssets.filter(a => a.assetName.trim()).length} assets</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addCreativeAsset}
                  >
                    <Plus size={16} />
                    Add Asset
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {creativeAssets.map((asset, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <div className="grid-row-3">
                          <select
                            value={asset.assetType}
                            onChange={(e) => handleCreativeAssetChange(index, 'assetType', e.target.value)}
                            className="form-input"
                          >
                            <option value="Image">Image</option>
                            <option value="Video">Video</option>
                            <option value="Banner">Banner</option>
                            <option value="Logo">Logo</option>
                            <option value="Document">Document</option>
                            <option value="Audio">Audio</option>
                            <option value="Other">Other</option>
                          </select>
                          <input
                            type="text"
                            value={asset.assetName}
                            onChange={(e) => handleCreativeAssetChange(index, 'assetName', e.target.value)}
                            className="form-input"
                            placeholder="Asset name"
                          />
                          <input
                            type="url"
                            value={asset.assetUrl}
                            onChange={(e) => handleCreativeAssetChange(index, 'assetUrl', e.target.value)}
                            className="form-input"
                            placeholder="Asset URL"
                          />
                        </div>
                        <div className="field-actions">
                          {creativeAssets.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeCreativeAsset(index)}
                              title="Remove asset"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team Section */}
              <div className="section-header">
                <h3>Team & Stakeholders</h3>
              </div>
              
              {/* Team Members */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Users size={20} className="text-blue-500" />
                    <h4>Team Members</h4>
                    <span className="field-count">{teamMembers.filter(m => m.user).length} members</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTeamMember}
                  >
                    <Plus size={16} />
                    Add Member
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {teamMembers.map((member, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <div className="grid-row-2">
                          <input
                            type="text"
                            value={member.user}
                            onChange={(e) => handleTeamMemberChange(index, 'user', e.target.value)}
                            className="form-input"
                            placeholder="User ID or email"
                          />
                          <select
                            value={member.role}
                            onChange={(e) => handleTeamMemberChange(index, 'role', e.target.value)}
                            className="form-input"
                          >
                            <option value="Manager">Manager</option>
                            <option value="Creator">Creator</option>
                            <option value="Analyst">Analyst</option>
                            <option value="Reviewer">Reviewer</option>
                            <option value="Approver">Approver</option>
                          </select>
                        </div>
                        <div className="field-actions">
                          {teamMembers.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTeamMember(index)}
                              title="Remove member"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stakeholders */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Users size={20} className="text-green-500" />
                    <h4>Stakeholders</h4>
                    <span className="field-count">{stakeholders.filter(s => s.name.trim()).length} stakeholders</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addStakeholder}
                  >
                    <Plus size={16} />
                    Add Stakeholder
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {stakeholders.map((stakeholder, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <div className="grid-row-2">
                          <input
                            type="text"
                            value={stakeholder.name}
                            onChange={(e) => handleStakeholderChange(index, 'name', e.target.value)}
                            className="form-input"
                            placeholder="Stakeholder name"
                          />
                          <input
                            type="email"
                            value={stakeholder.email}
                            onChange={(e) => handleStakeholderChange(index, 'email', e.target.value)}
                            className="form-input"
                            placeholder="Email address"
                          />
                        </div>
                        <div className="grid-row-2">
                          <input
                            type="text"
                            value={stakeholder.role}
                            onChange={(e) => handleStakeholderChange(index, 'role', e.target.value)}
                            className="form-input"
                            placeholder="Role"
                          />
                          <input
                            type="text"
                            value={stakeholder.department}
                            onChange={(e) => handleStakeholderChange(index, 'department', e.target.value)}
                            className="form-input"
                            placeholder="Department"
                          />
                        </div>
                        <div className="field-actions">
                          {stakeholders.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeStakeholder(index)}
                              title="Remove stakeholder"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleReset}
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              <Save size={16} />
              Create Campaign
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Campaign Master Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default CampaignMasterForm;
